import { GetServerSideProps } from 'next';
import Head from 'next/head';

import { SiteInfo } from '@/globals';
import { ArrowLeft, TicketPercent } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { getCategoriesByProducts } from '@/js/repository/CategoryRepository';
import { getPromoCodeProducts, getPromoCodeProductsCount, ProductResponse } from '@/js/repository/ProductRepository';
import { buildKeywordContent } from '@/lib/seo';
import { ProductList } from './products';

interface PromotionPageProps {
  products: ProductResponse[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  siteUrl: string;
  categories: unknown[];
}

const ITEMS_PER_PAGE = 24;

const getPageHref = (page: number) => page <= 1 ? '/promotions' : `/promotions?page=${page}`;

export const getServerSideProps: GetServerSideProps<PromotionPageProps> = async (context) => {
  const locale = context.locale ?? 'ko';
  const siteUrl = `https://${context.req.headers.host}`;
  const requestedPage = Math.max(1, Number(context.query.page) || 1);

  const categories = await getCategoriesByProducts(locale);
  const totalCount = await getPromoCodeProductsCount(locale);
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const currentPage = Math.min(requestedPage, totalPages);
  const products = await getPromoCodeProducts(locale, currentPage, ITEMS_PER_PAGE);

  if (requestedPage !== currentPage) {
    return {
      redirect: {
        destination: getPageHref(currentPage),
        permanent: false,
      },
    };
  }

  return {
    props: {
      products,
      totalCount,
      totalPages,
      currentPage,
      siteUrl,
      categories,
    },
  };
};

export default function PromotionPage({
  products,
  totalCount,
  totalPages,
  currentPage,
  siteUrl,
}: PromotionPageProps) {
  const { t } = useTranslation();
  const canonicalUrl = currentPage > 1 ? `${siteUrl}/promotions?page=${currentPage}` : `${siteUrl}/promotions`;

  return (
    <>
      <Head>
        <title>{`${t("promotions") || "Promotions"} | ${SiteInfo.siteName}`}</title>
        <meta name="description" content="프로모션 코드가 적용된 알리익스프레스 핫딜 상품을 점수순으로 확인하세요." />
        <meta
          name="keywords"
          content={buildKeywordContent([
            ...SiteInfo.primaryKeywords,
            '프로모션 코드',
            '쿠폰 코드',
            '알리익스프레스 쿠폰',
            '핫딜',
          ])}
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={`${t("promotions") || "Promotions"} | ${SiteInfo.siteName}`} />
        <meta property="og:description" content="프로모션 코드가 있는 상품만 모아 score 순으로 보여드립니다." />
        <meta property="og:image" content={`${siteUrl}/logo.png`} />
        <meta property="og:site_name" content={SiteInfo.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${siteUrl}/logo.png`} />
        <meta name="twitter:title" content={`${t("promotions") || "Promotions"} | ${SiteInfo.siteName}`} />
        <meta name="twitter:description" content="프로모션 코드가 있는 알리익스프레스 핫딜 상품 모음" />
      </Head>

      <div className="mx-auto max-w-[1360px] py-2">
        <div className="mb-6 flex justify-between">
          <a
            href="/"
            className="flex items-center text-sm font-semibold text-gray-500 hover:text-[#fd384f]"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("backToHome") || "Back to Home"}
          </a>
        </div>

        <section className="mb-6 rounded-lg border border-black/10 bg-white p-5 shadow-sm sm:p-6">
          <p className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-normal text-[#fd384f]">
            <TicketPercent className="h-4 w-4" />
            {t("promotions")}
          </p>
          <h1 className="mt-2 text-2xl font-black text-[#101820] sm:text-3xl">프로모션 코드 상품</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">
            등록된 상품 중 쿠폰 코드가 살아있는 상품만 모아 score 순으로 정렬했습니다.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <span className="rounded-md bg-[#fff1f2] px-3 py-1.5 font-black text-[#b91c1c]">
              {totalCount.toLocaleString()}개
            </span>
            {totalCount > ITEMS_PER_PAGE && (
              <span>
                {t("showingCount", {
                  from: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                  to: Math.min(currentPage * ITEMS_PER_PAGE, totalCount),
                  total: totalCount,
                })}
              </span>
            )}
          </div>
        </section>

        {products.length === 0 ? (
          <div className="rounded-lg border border-black/10 bg-white p-6 text-center text-gray-500">
            {t("noPromotions") || "No promotions available"}
          </div>
        ) : (
          <ProductList products={products} />
        )}

        {totalPages > 1 && (
          <div className="mt-8">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href={getPageHref(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }).map((_, i) => {
                  const pageNumber = i + 1;

                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink href={getPageHref(pageNumber)} isActive={pageNumber === currentPage}>
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }

                  if (
                    (pageNumber === 2 && currentPage > 3) ||
                    (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                  ) {
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }

                  return null;
                })}

                <PaginationItem>
                  <PaginationNext
                    href={getPageHref(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </>
  );
}
